import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import {
    decodeIsoDuration,
    emptyIsoValue,
    encodeIsoDuration,
    normalizeIsoDuration,
} from '../../services/isoDuration';

import './UnitNumberField.scss';

const UNIT_I18N = {
    year: 'quantity_unit_year',
    month: 'quantity_unit_month',
    day: 'quantity_unit_day',
    minute: 'quantity_unit_minute',
    seat: 'quantity_unit_seat',
    px: 'unit_px',
    pt: 'unit_pt',
    cm: 'unit_cm',
    chf: 'quantity_unit_chf',
    chf_week: 'quantity_unit_chf_week',
    points: 'quantity_unit_points',
    unit: 'quantity_unit_unit',
};

/**
 * Resolve the next stored value. When `clamp` is true, enforce min/max and report
 * whether the value was adjusted (for the inline info bubble).
 */
export const resolve_unit_number = (n, { min, max, isIso, unit, clamp = false } = {}) => {
    if (n === '' || n == null) {
        return {
            next: isIso ? emptyIsoValue(unit) : null,
            adjusted: null,
        };
    }
    const raw = Math.floor(Number(n));
    if (Number.isNaN(raw)) {
        return { next: undefined, adjusted: null };
    }
    // Clamp only on blur: while typing, intermediates below min (e.g. 10 when
    // min is 15 toward 100) must stay as typed.
    const v = clamp ? Math.min(max, Math.max(min, raw)) : raw;
    let adjusted = null;
    if (clamp && v !== raw) {
        adjusted = raw < min ? 'below_min' : 'above_max';
    }
    return {
        next: isIso ? encodeIsoDuration(v, unit) : v,
        adjusted,
        bound: adjusted === 'below_min' ? min : adjusted === 'above_max' ? max : undefined,
    };
};

/**
 * @param {string} [valueFormat] `number` (default) — native integer; `iso8601` — ISO duration string, encoding per `unit` (e.g. `PTnM`, `PnY`, `PnD`; `P`+`M` = months without `T`)
 */
const UnitNumberField = ({
    input,
    meta,
    min = 1,
    max = 9999,
    unit = 'year',
    valueFormat = 'number',
    disabled = false,
}) => {
    const { t } = useTranslation();
    const i18nKey = UNIT_I18N[unit] || UNIT_I18N.year;
    const isIso = valueFormat === 'iso8601';
    const [adjustment, setAdjustment] = useState(null);
    const [flash, setFlash] = useState(false);

    useEffect(() => {
        if (!isIso) {
            return;
        }
        // Do not coerce missing values to PT0M on mount: during section remounts
        // the field can briefly see an empty value, and writing PT0M dirties the
        // form so enableReinitialize keeps 0 instead of the real duration.
        if (input.value == null || input.value === '') {
            return;
        }
        const normalized = normalizeIsoDuration(input.value, unit);
        if (normalized !== input.value) {
            input.onChange(normalized);
        }
    // input object identity changes every render from redux-form; value/onChange suffice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIso, input.value, input.onChange, unit]);

    useEffect(() => {
        if (!flash) {
            return undefined;
        }
        const timer = setTimeout(() => setFlash(false), 1400);
        return () => clearTimeout(timer);
    }, [flash]);

    const displayNumber = useMemo(() => {
        if (isIso) {
            return decodeIsoDuration(input.value, unit);
        }
        const n = parseInt(input.value, 10);
        return Number.isFinite(n) ? n : '';
    }, [input.value, isIso, unit]);

    const countForLabel = useMemo(() => {
        if (isIso) {
            const m = decodeIsoDuration(input.value, unit);
            return m === '' || m == null ? 0 : Math.max(0, Number(m));
        }
        const n = parseInt(input.value, 10);
        if (input.value === '' || input.value == null) return 0;
        return Number.isFinite(n) ? Math.max(0, n) : 0;
    }, [input.value, isIso, unit]);

    const suffix = ['px', 'pt', 'cm', 'chf', 'chf_week', 'points'].includes(unit)
        ? t(`common.${UNIT_I18N[unit] || UNIT_I18N.year}`)
        : t(`common.${i18nKey}`, { count: countForLabel || 0 });

    const emit = useCallback(
        (n, { clamp = false } = {}) => {
            const { next, adjusted, bound } = resolve_unit_number(n, {
                min, max, isIso, unit, clamp,
            });
            if (next === undefined) {
                return { next: input.value, adjusted: null };
            }
            if (clamp) {
                const current = isIso
                    ? normalizeIsoDuration(input.value, unit)
                    : (input.value === '' || input.value == null
                        ? null
                        : Math.floor(Number(input.value)));
                if (next === current || (!isIso && current === next)) {
                    return { next: current, adjusted: null };
                }
            }
            input.onChange(next);
            if (adjusted) {
                setAdjustment({ kind: adjusted, bound });
                setFlash(true);
            }
            return { next, adjusted, bound };
        },
        [input, isIso, min, max, unit]
    );

    const onChange = useCallback(
        (e) => {
            setAdjustment(null);
            const v = e.target.value;
            if (v === '') {
                input.onChange(isIso ? emptyIsoValue(unit) : null);
                return;
            }
            emit(v);
        },
        [emit, isIso, input, unit]
    );

    const onBlur = useCallback(
        () => {
            // Pass the *value* to redux-form onBlur, never the DOM event: handleBlur
            // would otherwise re-read e.target.value and overwrite a just-clamped value.
            let final_value = input.value === '' || input.value == null
                ? (isIso ? emptyIsoValue(unit) : null)
                : input.value;

            if (isIso) {
                const normalized = normalizeIsoDuration(input.value, unit);
                const decoded = decodeIsoDuration(normalized, unit);
                if (decoded === '' || decoded == null) {
                    if (normalized !== input.value) {
                        input.onChange(normalized);
                    }
                    final_value = normalized;
                } else {
                    const { next } = emit(decoded, { clamp: true });
                    final_value = next;
                }
            } else if (input.value !== '' && input.value != null) {
                const { next } = emit(input.value, { clamp: true });
                final_value = next;
            }

            input.onBlur?.(final_value);
        },
        [emit, isIso, input, unit]
    );

    const dismissAdjustment = useCallback(() => setAdjustment(null), []);

    const adjustmentKey = adjustment
        ? (unit === 'seat'
            ? `unit_number_field.adjusted_${adjustment.kind}_seat`
            : `unit_number_field.adjusted_${adjustment.kind}`)
        : null;

    return (
        <div className="UnitNumberField-wrap">
            <InputGroup className="UnitNumberField">
                <Input
                    name={input.name}
                    type="number"
                    min={min}
                    max={max}
                    step={1}
                    className={`form-control UnitNumberField-input${flash ? ' UnitNumberField__flash' : ''}`}
                    value={displayNumber === '' ? '' : String(displayNumber)}
                    onChange={onChange}
                    onBlur={onBlur}
                    onFocus={input.onFocus}
                    disabled={disabled}
                />
                <InputGroupAddon addonType="append">
                    <InputGroupText className="text-nowrap">{suffix}</InputGroupText>
                </InputGroupAddon>
            </InputGroup>
            {meta.touched && meta.error && <div className="text-danger small">{meta.error}</div>}
            {adjustment && (
                <div
                    className="alert alert-info alert-inline mt-2 mb-0 py-2 px-3 small UnitNumberField__adjustment-note"
                    role="button"
                    tabIndex={0}
                    onClick={dismissAdjustment}
                    onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            dismissAdjustment();
                        }
                    }}
                >
                    <span className="alert-inline-body">
                        {t(adjustmentKey, {
                            min: adjustment.kind === 'below_min' ? adjustment.bound : min,
                            max: adjustment.kind === 'above_max' ? adjustment.bound : max,
                        })}
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * Controlled unit number input (outside redux-form).
 */
export const LayoutUnitNumberInput = ({ value, onChange, unit = 'px', min = 0, max = 9999, valueFormat = 'number', disabled = false }) => (
    <UnitNumberField
        input={{
            name: 'layout-unit-number',
            value: value ?? '',
            onChange: (v) => onChange?.(v === '' || v == null ? null : v),
        }}
        meta={{}}
        unit={unit}
        min={min}
        max={max}
        valueFormat={valueFormat}
        disabled={disabled}
    />
);

/**
 * Controlled decimal input with unit suffix (e.g. CHF, CHF/week).
 */
export const IntegerUnitInput = ({ value, onChange, unit = 'points', min = 0, max = 999999, disabled = false }) => {
    const { t } = useTranslation();
    const unitKey = UNIT_I18N[unit] || UNIT_I18N.points;
    const suffix = t(`common.${unitKey}`);

    const handleChange = (e) => {
        const raw = e.target.value;
        if (raw === '') {
            onChange?.(null);
            return;
        }
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed))
            return;
        onChange?.(Math.min(max, Math.max(min, parsed)));
    };

    return (
        <InputGroup className="UnitNumberField">
            <Input
                type="number"
                min={min}
                max={max}
                step={1}
                className="form-control UnitNumberField-input"
                value={value === null || typeof value === 'undefined' ? '' : String(value)}
                onChange={handleChange}
                disabled={disabled}
            />
            <InputGroupAddon addonType="append">
                <InputGroupText className="text-nowrap">{suffix}</InputGroupText>
            </InputGroupAddon>
        </InputGroup>
    );
};

export const DecimalUnitInput = ({ value, onChange, unit = 'chf', min = 0, max = 999999, disabled = false }) => {
    const { t } = useTranslation();
    const unitKey = UNIT_I18N[unit] || UNIT_I18N.chf;
    const suffix = t(`common.${unitKey}`);

    const handleChange = (e) => {
        const raw = e.target.value;
        if (raw === '') {
            onChange?.(null);
            return;
        }
        const parsed = parseFloat(raw);
        if (Number.isNaN(parsed))
            return;
        onChange?.(Math.min(max, Math.max(min, parsed)));
    };

    return (
        <InputGroup className="UnitNumberField">
            <Input
                type="number"
                min={min}
                max={max}
                step="0.01"
                className="form-control UnitNumberField-input"
                value={value === null || typeof value === 'undefined' ? '' : String(value)}
                onChange={handleChange}
                disabled={disabled}
            />
            <InputGroupAddon addonType="append">
                <InputGroupText className="text-nowrap">{suffix}</InputGroupText>
            </InputGroupAddon>
        </InputGroup>
    );
};

export default UnitNumberField;
